<template>
    <div :class="['ob-shell', step === 'celebration' ? 'ob-shell--celebration' : '']">

        <!-- Stuck-on-step nudge — only appears after idling on one step -->
        <div v-if="showStuckNudge" class="ob-stuck-nudge">
            <button type="button" class="ob-stuck-nudge__close" @click="dismissStuckNudge">&times;</button>
            <p class="mb-2">Stuck? We can show you where to click next.</p>
            <div class="d-flex" style="gap:8px;">
                <button type="button" class="btn btn-sm btn-primary" @click="showNextClickSpotlight">Show me</button>
                <a class="btn btn-sm btn-outline-secondary" href="https://wa.me/254755133133" target="_blank" rel="noopener">Chat with us</a>
            </div>
        </div>

        <!-- Spotlight: dims the page, leaves the next button to click lit up -->
        <div v-if="showSpotlight" class="ob-spotlight-backdrop" @click="closeSpotlight">
            <div class="ob-spotlight-hole" :style="spotlightStyle"></div>
            <p class="ob-spotlight-label" :style="spotlightLabelStyle">Click here ↑</p>
        </div>

        <!-- Thin progress bar fixed at the top -->
        <div v-if="showProgressBar" class="ob-progress-bar" :style="{ width: progressPercent + '%' }"></div>

        <div class="ob-inner">

            <!-- Back button -->
            <button v-if="canGoBack" class="ob-back" type="button" @click="goBack">
                <i class="bi bi-arrow-left"></i> Back
            </button>

            <transition :name="transitionName" mode="out-in">

                <!-- ── Loading (initial auth check) ───────────────────── -->
                <div v-if="step === 'loading'" key="loading" class="ob-loading">
                    <div class="spinner-border text-secondary" style="width:1.5rem;height:1.5rem;" role="status">
                        <span class="sr-only">Loading</span>
                    </div>
                </div>

                <!-- ── Signup ──────────────────────────────────────────── -->
                <div v-else-if="step === 'signup'" key="signup">
                    <span class="ob-brand-mark">{{ $platformBrand.name }}</span>
                    <h1 class="ob-heading">Manage your business the right way.</h1>
                    <p class="ob-subtext">Sales, stock, money and staff in one place. Free to start, ready in a minute.</p>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                    <div class="form-group mb-4">
                        <label class="ob-label">Your name</label>
                        <input
                            ref="signupNameInput"
                            v-model="signupName"
                            type="text"
                            class="form-control form-control-lg ob-input"
                            placeholder="e.g. Jane Njeri"
                            autocomplete="name"
                            @keyup.enter="$refs.signupPhoneInput && $refs.signupPhoneInput.focus()"
                        >
                        <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
                    </div>

                    <div class="form-group mb-4">
                        <label class="ob-label">Phone number</label>
                        <div class="ob-phone-row" :class="{ 'ob-phone-row--focus': phoneRowFocus }">
                            <span class="ob-phone-cc">🇰🇪 +254</span>
                            <input
                                ref="signupPhoneInput"
                                v-model="signupPhone"
                                type="tel"
                                class="ob-phone-input"
                                placeholder="712 345 678"
                                inputmode="numeric"
                                maxlength="10"
                                autocomplete="tel"
                                @input="signupPhone = signupPhone.replace(/\D/g,'').slice(0,10)"
                                @focus="phoneRowFocus = true"
                                @blur="phoneRowFocus = false"
                                @keyup.enter="submitSignup"
                            >
                        </div>
                        <small v-if="errors.phone" class="text-danger">{{ errors.phone }}</small>
                    </div>

                    <div class="form-check mb-4">
                        <input
                            id="ob-terms"
                            v-model="termsAccepted"
                            type="checkbox"
                            class="form-check-input ob-checkbox"
                        >
                        <label for="ob-terms" class="form-check-label ob-terms-label">
                            I agree to the <a href="/terms" target="_blank" class="ob-link">Terms of Service</a>
                            and <a href="/privacy" target="_blank" class="ob-link">Privacy Policy</a>
                        </label>
                    </div>

                    <!-- Referral / partner code -->
                    <div class="mb-4">
                        <button
                            v-if="!showReferralField"
                            type="button"
                            class="ob-referral-toggle"
                            @click="showReferralField = true"
                        >
                            <i class="bi bi-tag mr-1"></i> Have a partner or referral code?
                        </button>
                        <div v-else>
                            <label class="ob-label">Partner or referral code <span class="text-muted font-weight-normal">(optional)</span></label>
                            <input
                                v-model="referralCode"
                                type="text"
                                class="form-control form-control-lg ob-input"
                                placeholder="e.g. JANE2024"
                                autocomplete="off"
                                @input="referralCode = referralCode.toUpperCase()"
                            >
                            <small class="d-block mt-1" style="font-size:.72rem;color:#6c757d;">
                                <i class="bi bi-stars mr-1" style="color:#fd4719;"></i>
                                Enter <strong>DUKAKIT</strong> to unlock exclusive setup pricing.
                            </small>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        :disabled="isLoading"
                        @click="submitSignup"
                    >
                        <span v-if="isLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Sending code…
                        </span>
                        <span v-else>Get my verification code <i class="bi bi-arrow-right"></i></span>
                    </button>

                    <p class="text-center mt-3 ob-footer-link">
                        Already have an account?
                        <a href="/login" class="btn btn-link p-0 ob-inline-link">Sign in</a>
                    </p>
                </div>

                <!-- ── Login (returning user) ──────────────────────────── -->
                <div v-else-if="step === 'login-phone'" key="login-phone">
                    <span class="ob-brand-mark">{{ $platformBrand.name }}</span>
                    <h1 class="ob-heading">Welcome back</h1>
                    <p class="ob-subtext">Enter your phone number and we'll send you a sign-in code.</p>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                    <div class="form-group mb-4">
                        <label class="ob-label">Phone number</label>
                        <div class="ob-phone-row" :class="{ 'ob-phone-row--focus': phoneRowFocus }">
                            <span class="ob-phone-cc">🇰🇪 +254</span>
                            <input
                                ref="loginPhoneInput"
                                v-model="loginPhone"
                                type="tel"
                                class="ob-phone-input"
                                placeholder="712 345 678"
                                inputmode="numeric"
                                maxlength="10"
                                @input="loginPhone = loginPhone.replace(/\D/g,'').slice(0,10)"
                                @focus="phoneRowFocus = true"
                                @blur="phoneRowFocus = false"
                                @keyup.enter="submitLoginPhone"
                            >
                        </div>
                        <small v-if="errors.phone" class="text-danger">{{ errors.phone }}</small>
                    </div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        :disabled="isLoading"
                        @click="submitLoginPhone"
                    >
                        <span v-if="isLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Sending code…
                        </span>
                        <span v-else>Send code <i class="bi bi-arrow-right"></i></span>
                    </button>
                </div>

                <!-- ── OTP ─────────────────────────────────────────────── -->
                <div v-else-if="step === 'otp'" key="otp" class="text-center">
                    <div class="ob-otp-phone-icon mb-3">
                        <i class="bi bi-phone"></i>
                    </div>
                    <h1 class="ob-heading">Check your phone</h1>
                    <p class="ob-subtext">
                        We sent a 6-digit code to
                        <strong>+254 …{{ maskedPhone }}</strong>.
                    </p>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>
                    <div v-if="otpSuccess" class="ob-alert ob-alert--success mb-3">{{ otpSuccess }}</div>

                    <div class="ob-otp-boxes">
                        <input
                            v-for="i in 6"
                            :key="i"
                            :ref="'otp' + (i - 1)"
                            :value="otpDigits[i - 1]"
                            type="tel"
                            inputmode="numeric"
                            maxlength="1"
                            :class="['ob-otp-box', otpDigits[i - 1] ? 'ob-otp-box--filled' : '', isLoading ? 'ob-otp-box--loading' : '']"
                            autocomplete="one-time-code"
                            @input="onOtpDigit(i - 1, $event)"
                            @keydown="onOtpKey(i - 1, $event)"
                            @paste.prevent="onOtpPaste($event)"
                            @focus="$event.target.select()"
                        >
                    </div>

                    <p class="ob-resend-row">
                        Didn't get the code?
                        <span v-if="resendSeconds > 0" class="text-muted">Resend in {{ resendSeconds }}s</span>
                        <button
                            v-else
                            type="button"
                            class="btn btn-link p-0 ob-inline-link"
                            @click="resendOtp"
                        >Resend code</button>
                    </p>
                </div>

                <!-- ── Business name ───────────────────────────────────── -->
                <div v-else-if="step === 'business-name'" key="business-name">
                    <span class="ob-brand-mark">{{ $platformBrand.name }}</span>
                    <h1 class="ob-heading">What's your business called?</h1>
                    <p class="ob-subtext">You can change this anytime from your settings.</p>

                    <div class="form-group mb-4">
                        <input
                            ref="bizNameInput"
                            v-model="businessName"
                            type="text"
                            class="form-control form-control-lg ob-input"
                            placeholder="e.g. Mama Njeri Enterprises"
                            autocomplete="organization"
                            @keyup.enter="submitBusinessName"
                        >
                        <small v-if="errors.businessName" class="text-danger">{{ errors.businessName }}</small>
                    </div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        @click="submitBusinessName"
                    >
                        Continue <i class="bi bi-arrow-right"></i>
                    </button>
                </div>

                <!-- ── Business type ───────────────────────────────────── -->
                <div v-else-if="step === 'business-type'" key="business-type">
                    <h1 class="ob-heading">What kind of business is it?</h1>
                    <p class="ob-subtext">Tap to continue — we'll personalise your setup.</p>

                    <div class="ob-type-grid">
                        <div
                            v-for="type in businessTypes"
                            :key="type.id"
                            :class="['ob-type-tile', selectedBusinessType === type.id ? 'ob-type-tile--selected' : '']"
                            @click="selectType(type.id)"
                        >
                            <i :class="['bi', type.icon, 'ob-type-tile__icon']"></i>
                            <span class="ob-type-tile__name">{{ type.label }}</span>
                            <span class="ob-type-tile__sub">{{ type.sublabel }}</span>
                            <i
                                v-if="selectedBusinessType === type.id"
                                class="bi bi-check-circle-fill ob-type-tile__check"
                            ></i>
                        </div>
                    </div>
                </div>

                <!-- ── Primary use case ───────────────────────────────── -->
                <div v-else-if="step === 'primary-use-case'" key="primary-use-case">
                    <h1 class="ob-heading">How will you mainly use {{ $platformBrand.name }}?</h1>
                    <p class="ob-subtext">This decides what you see first when you log in.</p>

                    <div class="ob-type-grid">
                        <div
                            :class="['ob-type-tile', primaryUseCase === null ? 'ob-type-tile--selected' : '']"
                            @click="selectPrimaryUseCase(null)"
                        >
                            <i class="bi bi-receipt ob-type-tile__icon"></i>
                            <span class="ob-type-tile__name">Selling to walk-in customers</span>
                            <span class="ob-type-tile__sub">Your sales screen opens first</span>
                            <i v-if="primaryUseCase === null" class="bi bi-check-circle-fill ob-type-tile__check"></i>
                        </div>
                        <div
                            :class="['ob-type-tile', primaryUseCase === 'invoices_quotes' ? 'ob-type-tile--selected' : '']"
                            @click="selectPrimaryUseCase('invoices_quotes')"
                        >
                            <i class="bi bi-file-earmark-text ob-type-tile__icon"></i>
                            <span class="ob-type-tile__name">Invoices &amp; Estimates only</span>
                            <span class="ob-type-tile__sub">You bill clients instead of selling at a counter</span>
                            <i v-if="primaryUseCase === 'invoices_quotes'" class="bi bi-check-circle-fill ob-type-tile__check"></i>
                        </div>
                    </div>

                    <div v-if="primaryUseCase === 'invoices_quotes'" class="ob-referral-highlight mb-3">
                        <i class="bi bi-info-circle-fill mr-2"></i>
                        <span>Your homepage will show invoices, estimates, and payments due — not the sales screen.</span>
                    </div>
                </div>

                <!-- ── Modules ─────────────────────────────────────────── -->
                <div v-else-if="step === 'modules'" key="modules">
                    <h1 v-if="isInvoiceOnly" class="ob-heading">Anything else you need?</h1>
                    <h1 v-else-if="hasDefaultModules" class="ob-heading">Which tools do you need?</h1>
                    <h1 v-else class="ob-heading">Your {{ businessNoun }} is set up</h1>

                    <p v-if="isInvoiceOnly" class="ob-subtext">
                        Invoicing and estimates are ready to go. Add anything else you need — optional.
                    </p>
                    <p v-else-if="hasDefaultModules" class="ob-subtext">
                        We've suggested the right tools. Remove anything that doesn't apply, or add more.
                    </p>
                    <p v-else class="ob-subtext">
                        Everything you need to start selling is included. You can add advanced tools anytime.
                    </p>

                    <!-- Always-on modules -->
                    <p class="ob-label mb-2">Always included</p>
                    <div class="ob-core-list mb-4">
                        <div v-for="m in coreModules" :key="m.label" class="ob-core-item">
                            <i :class="['bi', m.icon, 'ob-core-item__icon']"></i>
                            <span class="ob-core-item__label">{{ m.label }}</span>
                            <span class="ob-core-item__badge">Included</span>
                        </div>
                    </div>

                    <!-- Optional modules -->
                    <p class="ob-label mb-2">Optional modules</p>
                    <div class="ob-module-list">
                        <div
                            v-for="mod in sortedModules"
                            :key="mod.flag"
                            :class="['ob-module', selectedModules.includes(mod.flag) ? 'ob-module--on' : '']"
                            @click="toggleModule(mod.flag)"
                        >
                            <div class="ob-module__left">
                                <i :class="['bi', mod.icon, 'ob-module__icon']"></i>
                                <div class="ob-module__text">
                                    <p class="ob-module__title mb-0">{{ mod.label }}</p>
                                    <p class="ob-module__desc mb-0">{{ mod.desc }}</p>
                                </div>
                            </div>
                            <div class="ob-module__check">
                                <i v-if="selectedModules.includes(mod.flag)" class="bi bi-check-lg"></i>
                            </div>
                        </div>
                    </div>

                    <div v-if="error" class="ob-alert ob-alert--error mt-3">{{ error }}</div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta mt-4"
                        :disabled="isLoading"
                        @click="confirmModules"
                    >
                        <span v-if="isLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Setting up…
                        </span>
                        <span v-else>Confirm &amp; continue <i class="bi bi-arrow-right"></i></span>
                    </button>
                </div>

                <!-- ── Affiliate payment wall ─────────────────────────── -->
                <div v-else-if="step === 'affiliate-payment'" key="affiliate-payment">
                    <div class="ob-affiliate-wall-icon mb-3">
                        <i class="bi bi-people-fill"></i>
                    </div>
                    <span class="ob-brand-mark">
                        {{ affiliateIsDukakit ? $platformBrand.name + ' Special Pricing' : $platformBrand.name + ' Partner Programme' }}
                    </span>
                    <h1 class="ob-heading">
                        {{ affiliateIsDukakit ? 'Your exclusive setup deal' : 'Pay your setup fee' }}
                    </h1>
                    <p class="ob-subtext">
                        <span v-if="affiliateIsDukakit">
                            You've unlocked {{ $platformBrand.name }}'s exclusive setup pricing. A one-time fee activates your
                            full account, priority onboarding, and direct support.
                        </span>
                        <span v-else-if="affiliateName">
                            You were onboarded by <strong>{{ affiliateName }}</strong> — a certified {{ $platformBrand.name }} partner.
                            Your one-time setup fee covers stock-taking, staff training, and on-site configuration.
                        </span>
                        <span v-else>
                            A one-time setup fee covers your stock-taking, staff training, and full account configuration.
                        </span>
                    </p>

                    <div v-if="affiliateIsDukakit" class="ob-referral-highlight mb-3">
                        <i class="bi bi-check-circle-fill mr-2" style="color:#28a745;"></i>
                        <span>Code applied — discounted rates unlocked!</span>
                    </div>

                    <div v-if="affiliatePayError" class="ob-alert ob-alert--error mb-3">{{ affiliatePayError }}</div>

                    <!-- Package selection -->
                    <div v-if="!affiliateStkSent">
                        <p class="ob-label mb-2">Select your package</p>
                        <div class="ob-option-list mb-4">
                            <label
                                v-for="pkg in affiliatePackages"
                                :key="pkg.value"
                                :class="['ob-option', affiliatePackage === pkg.value ? 'ob-option--on' : '']"
                            >
                                <input type="radio" :value="pkg.value" v-model="affiliatePackage" class="d-none">
                                <span :class="['ob-option__radio', affiliatePackage === pkg.value ? 'ob-option__radio--on' : '']">
                                    <i v-if="affiliatePackage === pkg.value" class="bi bi-check"></i>
                                </span>
                                <span class="ob-option__label">
                                    <template v-if="pkg.value === 'custom'">
                                        <strong>{{ pkg.label }}</strong>
                                    </template>
                                    <template v-else>
                                        <strong>KES {{ pkg.value.toLocaleString() }}</strong>
                                        <span class="text-muted ml-2">{{ pkg.label }}</span>
                                    </template>
                                </span>
                            </label>
                        </div>

                        <div v-if="affiliatePackage === 'custom'" class="mb-4">
                            <p class="ob-label mb-2">Enter amount (KES)</p>
                            <input
                                v-model="customFeeAmount"
                                type="number"
                                min="1"
                                step="1"
                                class="form-control"
                                placeholder="e.g. 4500"
                            >
                        </div>

                        <p class="ob-label mb-2">M-Pesa number to charge</p>
                        <div class="ob-phone-row mb-4" :class="{ 'ob-phone-row--focus': affiliatePhoneFocus }">
                            <span class="ob-phone-cc">🇰🇪 +254</span>
                            <input
                                v-model="affiliatePhone"
                                type="tel"
                                class="ob-phone-input"
                                placeholder="712 345 678"
                                inputmode="numeric"
                                maxlength="10"
                                @input="affiliatePhone = affiliatePhone.replace(/\D/g,'').slice(0,10)"
                                @focus="affiliatePhoneFocus = true"
                                @blur="affiliatePhoneFocus = false"
                            >
                        </div>

                        <button
                            type="button"
                            class="btn btn-primary btn-block ob-cta"
                            :disabled="!affiliatePackage || (affiliatePackage === 'custom' && !isCustomAmountValid) || !isAffiliatePhoneValid || affiliatePayLoading"
                            @click="initiateAffiliatePay"
                        >
                            <span v-if="affiliatePayLoading">
                                <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                Initiating payment…
                            </span>
                            <span v-else>Pay KES {{ effectiveFeeAmount ? effectiveFeeAmount.toLocaleString() : '' }} via M-Pesa <i class="bi bi-arrow-right"></i></span>
                        </button>
                    </div>

                    <!-- Waiting for M-Pesa confirmation -->
                    <div v-else class="text-center py-3">
                        <div class="ob-affiliate-wait-ring mb-3">
                            <div class="spinner-border text-dark" role="status">
                                <span class="sr-only">Waiting</span>
                            </div>
                        </div>
                        <h2 class="ob-heading">Check your phone</h2>
                        <p class="ob-subtext">
                            An M-Pesa prompt has been sent to <strong>+254 …{{ affiliatePhone.slice(-4) }}</strong>.
                            Enter your PIN to complete payment.
                        </p>
                        <p class="text-muted" style="font-size:.8rem;">Waiting for confirmation… ({{ affiliatePollCountdown }}s)</p>

                        <button
                            type="button"
                            class="btn btn-link text-secondary btn-block mt-2 ob-skip"
                            @click="retryAffiliatePay"
                        >
                            Didn't get it? Try again
                        </button>
                    </div>
                </div>

                <!-- ── Document branding (invoice-only) ───────────────── -->
                <div v-else-if="step === 'document-branding'" key="document-branding">
                    <h1 class="ob-heading">Customize your invoices &amp; quotes</h1>
                    <p class="ob-subtext">This shows up on every invoice and estimate you send.</p>

                    <div v-if="documentSettingsLoading" class="text-center py-4">
                        <span class="spinner-border spinner-border-sm text-secondary"></span>
                    </div>

                    <template v-else-if="documentSettings">
                        <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                        <p class="ob-label mb-2">Brand color</p>
                        <div class="d-flex align-items-center mb-4" style="gap:10px;">
                            <input type="color" class="form-control p-1" style="max-width:56px;" v-model="documentSettings.companysettings.document_accent_color">
                            <input type="text" class="form-control" placeholder="#fd4719" v-model="documentSettings.companysettings.document_accent_color">
                        </div>

                        <div class="custom-control custom-checkbox mb-3">
                            <input type="checkbox" class="custom-control-input" id="obShowLogo" v-model="documentSettings.companysettings.document_show_logo">
                            <label class="custom-control-label" for="obShowLogo">Show my logo on documents</label>
                        </div>

                        <div class="d-flex align-items-center mb-4" style="gap:12px;">
                            <div class="ob-logo-preview">
                                <img v-if="documentSettings.logo_url" :src="documentSettings.logo_url" alt="Logo">
                                <i v-else class="bi bi-image"></i>
                            </div>
                            <div>
                                <input type="file" accept="image/png,image/jpeg" ref="logoInput" class="d-none" @change="uploadLogo">
                                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="logoUploading" @click="$refs.logoInput.click()">
                                    <span v-if="logoUploading"><span class="spinner-border spinner-border-sm mr-1"></span>Uploading…</span>
                                    <span v-else>{{ documentSettings.logo_url ? 'Change logo' : 'Upload logo' }}</span>
                                </button>
                                <p class="text-muted mb-0 mt-1" style="font-size:0.75rem;">PNG or JPG, max 2MB</p>
                            </div>
                        </div>

                        <p class="ob-label mb-2">Business address <span class="text-muted">— shown under your business name</span></p>
                        <input type="text" class="form-control mb-4" placeholder="e.g. Moi Avenue, Nairobi" v-model="documentSettings.address">

                        <p class="ob-label mb-2">Invoice notes <span class="text-muted">— shown near the totals</span></p>
                        <textarea class="form-control mb-3" rows="2" v-model="documentSettings.companysettings.invoice_default_notes"></textarea>
                        <p class="ob-label mb-2">Invoice terms <span class="text-muted">— shown as your payment terms</span></p>
                        <textarea class="form-control mb-4" rows="2" placeholder="e.g. Payment due within 14 days." v-model="documentSettings.companysettings.invoice_default_terms"></textarea>

                        <p class="ob-label mb-2">Estimate notes <span class="text-muted">— shown near the totals</span></p>
                        <textarea class="form-control mb-3" rows="2" v-model="documentSettings.companysettings.quote_default_notes"></textarea>
                        <p class="ob-label mb-2">Estimate terms <span class="text-muted">— shown as your validity terms</span></p>
                        <textarea class="form-control mb-4" rows="2" placeholder="e.g. Valid for 14 days from issue date." v-model="documentSettings.companysettings.quote_default_terms"></textarea>

                        <div class="d-flex mb-4" style="gap:10px;">
                            <button type="button" class="btn btn-outline-secondary btn-block" @click="openDocPreview('invoice')">
                                <i class="bi bi-eye mr-1"></i>Preview invoice
                            </button>
                            <button type="button" class="btn btn-outline-secondary btn-block" @click="openDocPreview('quote')">
                                <i class="bi bi-eye mr-1"></i>Preview estimate
                            </button>
                        </div>

                        <button
                            type="button"
                            class="btn btn-primary btn-block ob-cta"
                            :disabled="documentSettingsSaving"
                            @click="continueFromDocumentBranding"
                        >
                            <span v-if="documentSettingsSaving">
                                <span class="spinner-border spinner-border-sm mr-2" role="status"></span>Saving…
                            </span>
                            <span v-else>Save &amp; continue <i class="bi bi-arrow-right"></i></span>
                        </button>

                        <div class="d-flex mt-3" style="gap:10px;">
                            <button type="button" class="btn btn-link text-secondary flex-fill ob-skip" :disabled="isLoading" @click="finishAndCreateDocument('/invoices/create')">
                                Save &amp; create invoice
                            </button>
                            <button type="button" class="btn btn-link text-secondary flex-fill ob-skip" :disabled="isLoading" @click="finishAndCreateDocument('/quotes/create')">
                                Save &amp; create estimate
                            </button>
                        </div>
                    </template>

                    <document-preview-modal
                        v-if="showDocPreview"
                        :doc-type="docPreviewType === 'quote' ? 'Quote' : 'Invoice'"
                        :customer="{ full_name: 'Jane Sample Customer' }"
                        :items="[{ _key: 1, description: 'Sample service', item_note: '', quantity: 1, unit_price: 5000, discount: 0, tax: null }]"
                        :notes="docPreviewType === 'quote' ? documentSettings.companysettings.quote_default_notes : documentSettings.companysettings.invoice_default_notes"
                        :terms="docPreviewType === 'quote' ? documentSettings.companysettings.quote_default_terms : documentSettings.companysettings.invoice_default_terms"
                        :date="new Date().toISOString()"
                        :totals="{ subtotal: 5000, discount: 0, tax: 0, total: 5000 }"
                        :company-override="documentSettings"
                        :accent-override="documentSettings.companysettings.document_accent_color"
                        @close="showDocPreview = false"
                    ></document-preview-modal>
                </div>

                <!-- ── Activity hub ───────────────────────────────────── -->
                <div v-else-if="step === 'activity-hub'" key="activity-hub">
                    <span class="ob-brand-mark">{{ $platformBrand.name }}</span>
                    <h1 class="ob-heading">
                        {{ businessName ? businessName + ' is ready.' : 'Your ' + businessNoun + ' is ready.' }}
                    </h1>
                    <p class="ob-subtext">What would you like to do first?</p>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                    <div class="ob-hub-cards">
                        <div
                            v-for="card in activityCards"
                            :key="card.id"
                            :class="['ob-hub-card', card.primary ? 'ob-hub-card--primary' : '']"
                            @click="handleActivity(card)"
                        >
                            <div class="ob-hub-card__icon-wrap">
                                <i :class="['bi', card.icon]"></i>
                            </div>
                            <div class="ob-hub-card__body">
                                <p class="ob-hub-card__title mb-0">{{ card.title }}</p>
                                <p class="ob-hub-card__desc mb-0">{{ card.desc }}</p>
                            </div>
                            <i class="bi bi-chevron-right ob-hub-card__arrow"></i>
                        </div>
                    </div>

                    <div v-if="isLoading" class="text-center mt-3">
                        <span class="spinner-border spinner-border-sm text-secondary" role="status"></span>
                    </div>

                    <button
                        type="button"
                        class="btn btn-link text-secondary btn-block mt-3 ob-skip"
                        :disabled="isLoading"
                        @click="skipToShop"
                    >
                        Skip, go to my dashboard
                    </button>
                </div>

                <!-- ── Add product ─────────────────────────────────────── -->
                <div v-else-if="step === 'add-product'" key="add-product">
                    <h1 class="ob-heading">Add your first {{ terminology.productLabel }}</h1>
                    <p class="ob-subtext">Just a name and price to get started.</p>

                    <div class="form-group mb-4">
                        <label class="ob-label">{{ terminology.productLabel | capitalize }} name</label>
                        <input
                            ref="productNameInput"
                            v-model="product.name"
                            type="text"
                            class="form-control form-control-lg ob-input"
                            :placeholder="terminology.productPlaceholder"
                            autocomplete="off"
                            @keyup.enter="$refs.productPriceInput && $refs.productPriceInput.focus()"
                        >
                    </div>

                    <div class="form-group mb-4">
                        <label class="ob-label">Price (KES)</label>
                        <input
                            ref="productPriceInput"
                            v-model.number="product.price"
                            type="number"
                            class="form-control form-control-lg ob-input"
                            placeholder="0"
                            min="0"
                            step="1"
                            @keyup.enter="submitProduct"
                        >
                    </div>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        :disabled="!product.name || !product.price || isLoading"
                        @click="submitProduct"
                    >
                        <span v-if="isLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Adding…
                        </span>
                        <span v-else>Add {{ terminology.productLabel }} <i class="bi bi-arrow-right"></i></span>
                    </button>

                    <button
                        type="button"
                        class="btn btn-link text-secondary btn-block mt-2 ob-skip"
                        @click="showBulkImportInfo = true"
                    >
                        Already have a list of {{ terminology.productLabel }}s? <i class="bi bi-arrow-right"></i>
                    </button>
                </div>

                <!-- ── Product added ───────────────────────────────────── -->
                <div v-else-if="step === 'product-added'" key="product-added" class="text-center">
                    <div class="ob-success-ring mb-4">
                        <i class="bi bi-check-lg"></i>
                    </div>

                    <h1 class="ob-heading">
                        <span class="d-block text-truncate ob-product-name-display">{{ createdProductName }}</span>
                        {{ terminology.addedLabel }}
                    </h1>
                    <p class="ob-subtext">{{ terminology.addedSubtext }}</p>

                    <div v-if="hasAffiliate" class="ob-affiliate-note mb-4">
                        <i class="bi bi-person-check-fill mr-2"></i>
                        Your {{ $platformBrand.name }} partner will be in touch to help configure the rest.
                    </div>

                    <div v-if="error" class="ob-alert ob-alert--error mb-3">{{ error }}</div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        :disabled="isLoading"
                        @click="startFirstSale"
                    >
                        <span v-if="isLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Getting ready…
                        </span>
                        <span v-else>{{ terminology.saleCta }} <i class="bi bi-arrow-right"></i></span>
                    </button>

                    <button
                        type="button"
                        class="btn btn-link text-secondary btn-block mt-2 ob-skip"
                        :disabled="isLoading"
                        @click="skipToShop"
                    >
                        Skip, go to my dashboard
                    </button>
                </div>

                <!-- ── First sale ──────────────────────────────────────── -->
                <div v-else-if="step === 'first-sale'" key="first-sale">
                    <h1 class="ob-heading">{{ terminology.saleTitle }}</h1>

                    <div v-if="createdProduct" class="ob-sale-card mt-4">
                        <div class="d-flex justify-content-between align-items-start">
                            <div>
                                <p class="ob-sale-card__name mb-0">{{ createdProduct.product }}</p>
                                <p class="ob-sale-card__price mb-0">KES {{ formatAmount(createdProduct.ppu) }} each</p>
                            </div>
                            <div class="ob-qty-row">
                                <button type="button" class="ob-qty-btn" :disabled="saleQty <= 1" @click="saleQty--">−</button>
                                <span class="ob-qty-val">{{ saleQty }}</span>
                                <button type="button" class="ob-qty-btn" @click="saleQty++">+</button>
                            </div>
                        </div>
                        <hr class="my-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="ob-label mb-0">Total</span>
                            <span class="ob-sale-total">KES {{ formatAmount(saleTotal) }}</span>
                        </div>
                    </div>

                    <div class="mt-4">
                        <p class="ob-label">Payment method</p>

                        <div v-if="saleLoadingPayments" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm text-secondary" role="status"></span>
                        </div>

                        <div v-else class="ob-option-list">
                            <label
                                v-for="method in salePaymentTypes"
                                :key="method.id"
                                :class="['ob-option', saleSelectedMethod && saleSelectedMethod.id === method.id ? 'ob-option--on' : '']"
                            >
                                <input type="radio" :value="method" v-model="saleSelectedMethod" class="d-none">
                                <span :class="['ob-option__radio', saleSelectedMethod && saleSelectedMethod.id === method.id ? 'ob-option__radio--on' : '']">
                                    <i v-if="saleSelectedMethod && saleSelectedMethod.id === method.id" class="bi bi-check"></i>
                                </span>
                                <span class="ob-option__label">{{ method.payment_method }}</span>
                            </label>
                        </div>
                    </div>

                    <transition name="ob-fade">
                        <div v-if="saleSelectedMethod && saleSelectedMethod.payment_method === 'Cash'" class="mt-3">
                            <label class="ob-label">Amount received (KES)</label>
                            <input
                                v-model.number="saleCashAmount"
                                type="number"
                                class="form-control form-control-lg ob-input"
                                placeholder="0"
                                min="0"
                                step="1"
                            >
                            <p v-if="saleChange > 0" class="text-muted mt-2" style="font-size:.85rem;">
                                Change: KES {{ formatAmount(saleChange) }}
                            </p>
                        </div>
                    </transition>

                    <div v-if="error" class="ob-alert ob-alert--error mt-3">{{ error }}</div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta mt-4"
                        :disabled="!saleCanComplete || saleProcessing"
                        @click="completeSale"
                    >
                        <span v-if="saleProcessing">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Processing…
                        </span>
                        <span v-else>{{ terminology.completeCta }} <i class="bi bi-arrow-right"></i></span>
                    </button>
                </div>

                <!-- ── Celebration ─────────────────────────────────────── -->
                <div v-else-if="step === 'celebration'" key="celebration" class="ob-celebration text-center">
                    <div class="ob-celebration-ring mb-3">
                        <i class="bi bi-check-lg"></i>
                    </div>

                    <p class="ob-celebration-badge">{{ terminology.badge }}</p>
                    <p class="ob-celebration-amount">KES {{ formatAmount(saleDisplayAmount) }}</p>
                    <h1 class="ob-heading">{{ terminology.heading }}</h1>
                    <p class="ob-subtext">
                        {{ businessName || 'Your ' + businessNoun }}
                        <span class="mx-1 text-muted">·</span>
                        {{ saleDoneLabel }}
                    </p>

                    <div v-if="terminology.nudge" class="ob-feature-nudge mb-4">
                        <i :class="['bi', terminology.nudge.icon, 'mr-2']"></i>
                        {{ terminology.nudge.text }}
                    </div>

                    <button
                        type="button"
                        class="btn btn-outline-primary btn-block ob-cta-secondary mb-2"
                        @click="shareOnWhatsApp"
                    >
                        <i class="bi bi-whatsapp mr-2"></i> Share receipt
                    </button>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        @click="navigate('credentials')"
                    >
                        Go to my {{ businessNoun }} <i class="bi bi-arrow-right"></i>
                    </button>
                </div>

                <!-- ── Credentials ────────────────────────────────────────── -->
                <div v-else-if="step === 'credentials'" key="credentials">
                    <div class="ob-success-ring ob-success-ring--sm ob-success-ring--grey mb-4">
                        <i class="bi bi-shield-lock-fill"></i>
                    </div>

                    <h1 class="ob-heading text-center">
                        {{ credentialsIsEarlyStep ? 'Secure your account' : 'Keep your account safe' }}
                    </h1>
                    <p class="ob-subtext text-center">
                        {{ credentialsIsEarlyStep
                            ? 'Add an email and password — so you can always sign in, even without your phone.'
                            : 'Add an email and password so you can log in from any device.' }}
                    </p>

                    <div v-if="credentialsError" class="ob-alert ob-alert--error mb-3">{{ credentialsError }}</div>
                    <div v-if="credentialsSuccess" class="ob-alert ob-alert--success mb-3">{{ credentialsSuccess }}</div>

                    <div class="form-group mb-3">
                        <label class="ob-label">Email address</label>
                        <input
                            v-model="credentials.email"
                            type="email"
                            class="form-control form-control-lg ob-input"
                            placeholder="you@example.com"
                            autocomplete="email"
                        >
                    </div>

                    <div class="form-group mb-4">
                        <label class="ob-label">Password</label>
                        <div class="ob-pw-row">
                            <input
                                :type="credPasswordVisible ? 'text' : 'password'"
                                v-model="credentials.password"
                                class="form-control form-control-lg ob-input ob-pw-input"
                                placeholder="At least 8 characters"
                                autocomplete="new-password"
                            >
                            <button
                                type="button"
                                class="ob-pw-toggle"
                                tabindex="-1"
                                @click="credPasswordVisible = !credPasswordVisible"
                            >
                                <i :class="credPasswordVisible ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                            </button>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="btn btn-primary btn-block ob-cta"
                        :disabled="credentialsLoading"
                        @click="saveCredentials"
                    >
                        <span v-if="credentialsLoading">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Saving…
                        </span>
                        <span v-else>
                            {{ credentialsIsEarlyStep ? ('Save & set up my ' + businessNoun) : ('Save and go to my ' + businessNoun) }}
                            <i class="bi bi-arrow-right"></i>
                        </span>
                    </button>

                    <button
                        type="button"
                        class="btn btn-link text-secondary btn-block mt-2 ob-skip"
                        @click="skipCredentials"
                    >
                        Skip for now
                    </button>
                </div>

                <!-- ── Finishing ───────────────────────────────────────── -->
                <div v-else key="finishing" class="ob-finishing text-center">
                    <div class="spinner-border text-dark" role="status">
                        <span class="sr-only">Loading</span>
                    </div>
                    <p class="ob-finishing-msg">{{ finishingMessage }}</p>
                </div>

            </transition>

            <!-- ── Bulk import info modal ───────────────────────────── -->
            <transition name="ob-fade">
                <div v-if="showBulkImportInfo" class="ob-modal-overlay" @click.self="showBulkImportInfo = false">
                    <div class="ob-modal-panel">
                        <button type="button" class="ob-modal-close" @click="showBulkImportInfo = false">
                            <i class="bi bi-x-lg"></i>
                        </button>
                        <div class="ob-modal-icon mb-3">
                            <i class="bi bi-file-earmark-spreadsheet"></i>
                        </div>
                        <h2 class="ob-heading" style="font-size:1.25rem;">Bulk import your products</h2>
                        <p class="ob-subtext">
                            Got a long list already? Skip typing them one by one — download our template, fill in
                            your products, then upload it from <strong>Inventory → Import</strong> once you're in
                            your dashboard.
                        </p>
                        <ol class="ob-modal-steps">
                            <li>Download the template below</li>
                            <li>Fill in item name, category, price, and quantity</li>
                            <li>Go to Inventory → Import in your dashboard and upload it</li>
                        </ol>
                        <a href="/files/DUKAKIT_Store_Manager.xlsx" download class="btn btn-primary btn-block ob-cta">
                            <i class="bi bi-download mr-1"></i> Download sample template
                        </a>
                        <button
                            type="button"
                            class="btn btn-link text-secondary btn-block mt-2 ob-skip"
                            @click="showBulkImportInfo = false"
                        >
                            Got it, I'll add one product for now
                        </button>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
    import DocumentPreviewModal from './quotesInvoices/DocumentPreviewModal.vue';

    const BUSINESS_TYPES = [
        { id: 'retail',        label: 'Retail & Trade',     sublabel: 'Shops & stores',        icon: 'bi-shop',               defaultModules: [] },
        { id: 'wholesale',     label: 'Wholesale',          sublabel: 'Distributors & bulk',   icon: 'bi-truck',              defaultModules: [] },
        { id: 'food',          label: 'Food & Beverage',    sublabel: 'Restaurants & cafés',    icon: 'bi-egg-fried',          defaultModules: ['has_kds'] },
        { id: 'hospitality',   label: 'Hospitality',        sublabel: 'Hotels, lodges & BnBs',  icon: 'bi-building',           defaultModules: ['has_reservations'] },
        { id: 'fitness',       label: 'Fitness & Gyms',     sublabel: 'Gyms & fitness clubs',   icon: 'bi-activity',           defaultModules: ['has_subscriptions'] },
        { id: 'services',      label: 'Services',           sublabel: 'Salons, laundry, repairs…', icon: 'bi-scissors',        defaultModules: [] },
        { id: 'manufacturing', label: 'Manufacturing',      sublabel: 'Production & output',    icon: 'bi-gear-wide-connected', defaultModules: ['has_manufacturing'] },
        { id: 'other',         label: 'Something else',     sublabel: 'Any other business',     icon: 'bi-three-dots',         defaultModules: [] },
    ];

    const ALL_MODULES = [
        { flag: 'has_kds',           label: 'Restaurant & Food',        desc: 'Take orders and send them to a kitchen screen',   icon: 'bi-egg-fried' },
        { flag: 'has_reservations',  label: 'Rooms & Accommodation',    desc: 'Room types, check-ins, booking payments',          icon: 'bi-calendar2-check' },
        { flag: 'has_subscriptions', label: 'Memberships & Plans',      desc: 'Member plans, check-ins and renewals',             icon: 'bi-people-fill' },
        { flag: 'has_manufacturing', label: 'Manufacturing',            desc: 'Raw materials, recipes and production batches',    icon: 'bi-gear-wide-connected' },
        { flag: 'has_payroll',       label: 'Payroll & HR',             desc: 'Staff wages, leave tracking, attendance',          icon: 'bi-cash-stack' },
    ];

    const CORE_MODULES = [
        { label: 'Sales',                icon: 'bi-receipt' },
        { label: 'Inventory management', icon: 'bi-boxes' },
        { label: 'Customer records',     icon: 'bi-person-lines-fill' },
    ];

    const LEGACY_INDUSTRY_MAP = {
        'Retail': 'retail', 'Restaurant': 'food', 'Café': 'food', 'Café / Coffee Shop': 'food',
        'Hotel': 'hospitality', 'Gym / Fitness': 'fitness', 'Laundry / Dry Cleaning': 'services',
        'Services': 'services', 'Manufacturing': 'manufacturing', 'Other / Custom': 'other',
    };

    export default {
        name: 'OnboardingProcess',

        components: { DocumentPreviewModal },

        filters: {
            capitalize(v) { return v ? v.charAt(0).toUpperCase() + v.slice(1) : ''; },
        },

        data() {
            return {
                step:      'loading',
                direction: 'forward',
                stuckTimer:      null,
                showStuckNudge:  false,
                showSpotlight:   false,
                spotlightStyle:  {},
                spotlightLabelStyle: {},

                // Pre-auth
                signupName:      '',
                signupPhone:     '',
                termsAccepted:   false,
                loginPhone:      '',
                activePhone:     '',
                otpContext:      'signup',
                otpDigits:       ['', '', '', '', '', ''],
                otpSuccess:      '',
                resendSeconds:   0,
                resendInterval:  null,
                maskedPhone:     '',
                phoneRowFocus:   false,
                referralCode:    '',

                // Business profile
                businessName:         '',
                selectedBusinessType: null,
                selectedModules:      [],
                primaryUseCase:       null,

                // Product
                product:             { name: '', price: '' },
                createdProduct:      null,
                createdProductName:  '',

                // Sale
                saleQty:             1,
                salePaymentTypes:    [],
                saleSelectedMethod:  null,
                saleCashAmount:      '',
                saleProcessing:      false,
                saleLoadingPayments: false,
                saleCompletedAmount: 0,
                saleDisplayAmount:   0,
                saleDoneLabel:       '',

                // Credentials
                credentials:            { email: '', password: '' },
                credPasswordVisible:    false,
                credentialsLoading:     false,
                credentialsError:       '',
                credentialsSuccess:     '',
                credentialsIsEarlyStep: false, // true = shown right after OTP (new signup)

                // Global
                isLoading:        false,
                error:            null,
                errors:           {},
                hasAffiliate:     false,
                setupCompleted:   false,
                finishingMessage: 'Setting up your business…',
                showBulkImportInfo: false,

                // Signup referral code field
                showReferralField:  false,

                // Affiliate payment
                onboardingFeePaid:      false,
                affiliateIsDukakit:     false,
                affiliateName:          '',
                affiliatePackage:       null,
                customFeeAmount:        '',
                affiliatePhone:         '',
                affiliatePhoneFocus:    false,
                affiliatePayLoading:    false,
                affiliatePayError:      '',
                affiliateStkSent:       false,
                affiliatePollCountdown: 180,
                affiliatePollTimer:     null,
                affiliateBillingId:     null,

                // Document branding (invoice-only businesses)
                documentSettings:        null,
                documentSettingsLoading: false,
                documentSettingsSaving:  false,
                docPreviewType:          'invoice',
                showDocPreview:          false,
                logoUploading:           false,

                // Expose constants for template
                businessTypes: BUSINESS_TYPES,
            };
        },

        computed: {
            transitionName() {
                return this.direction === 'back' ? 'ob-slide-bck' : 'ob-slide-fwd';
            },

            canGoBack() {
                return ['login-phone', 'otp', 'business-type', 'primary-use-case', 'modules', 'document-branding', 'activity-hub', 'add-product', 'product-added', 'first-sale'].includes(this.step);
            },

            showProgressBar() {
                return !['loading', 'signup', 'login-phone', 'otp', 'celebration', 'finishing'].includes(this.step);
            },

            isAffiliatePhoneValid() {
                const d = (this.affiliatePhone || '').replace(/\D/g, '');
                return (d.length === 10 && (d.startsWith('07') || d.startsWith('01'))) ||
                       (d.length === 9  && (d.startsWith('7')  || d.startsWith('1')));
            },

            affiliatePackages() {
                if (this.affiliateIsDukakit) {
                    return [
                        { value: 500,  label: 'Starter — Quick digital setup' },
                        { value: 1000, label: 'Basic — Stock setup & onboarding' },
                        { value: 2000, label: 'Standard — Training + 1 month support' },
                        { value: 'custom', label: 'Custom amount' },
                    ];
                }
                return [
                    { value: 3000,  label: 'Basic — Stock setup & onboarding' },
                    { value: 5000,  label: 'Standard — Training + 1 month support' },
                    { value: 10000, label: 'Premium — Full setup + 3 month support' },
                    { value: 'custom', label: 'Custom amount' },
                ];
            },

            isCustomAmountValid() {
                const n = Number(this.customFeeAmount);
                return n > 0;
            },

            effectiveFeeAmount() {
                return this.affiliatePackage === 'custom' ? Number(this.customFeeAmount) : this.affiliatePackage;
            },

            progressPercent() {
                const map = {
                    'credentials':        10,
                    'affiliate-payment':  20,
                    'business-name':      32,
                    'business-type':      42,
                    'primary-use-case':   49,
                    'modules':            56,
                    'activity-hub':       62,
                    'document-branding':  70,
                    'add-product':        76,
                    'product-added':      86,
                    'first-sale':         93,
                };
                return map[this.step] || 0;
            },

            hasDefaultModules() {
                const type = BUSINESS_TYPES.find(t => t.id === this.selectedBusinessType);
                return !!(type && type.defaultModules.length > 0);
            },

            isInvoiceOnly() {
                return this.primaryUseCase === 'invoices_quotes';
            },

            coreModules() {
                if (this.isInvoiceOnly) {
                    return [
                        { label: 'Invoicing & Estimates', icon: 'bi-receipt' },
                        { label: 'Customer records',       icon: 'bi-person-lines-fill' },
                    ];
                }
                return CORE_MODULES;
            },

            sortedModules() {
                const checked   = ALL_MODULES.filter(m => this.selectedModules.includes(m.flag));
                const unchecked = ALL_MODULES.filter(m => !this.selectedModules.includes(m.flag));
                return [...checked, ...unchecked];
            },

            // Generic word for "your business" used outside the product/sale copy
            // (which already has its own per-vertical wording via terminology()).
            // Falls back to 'business' before a type is picked, or for verticals
            // where no single noun fits everyone (gym vs salon, laundry vs repairs…).
            businessNoun() {
                if (this.selectedBusinessType === 'retail') return 'shop';
                if (this.selectedBusinessType === 'food')   return 'restaurant';
                return 'business';
            },

            // The onboarding "first sale" step always records a plain till sale
            // (/api/order + /api/full-payment) whatever the vertical, so the copy
            // below must describe a sale/payment — never a booking, member,
            // production run or appointment that isn't actually created. The
            // nudge then points to where the real vertical workflow lives.
            terminology() {
                const mods = this.selectedModules;
                const type = this.selectedBusinessType;

                if (mods.includes('has_reservations')) {
                    return {
                        productLabel: 'room type', productPlaceholder: 'e.g. Standard Room',
                        addedLabel: 'is ready for guests.', addedSubtext: "Now let's take payment for your first room night.",
                        saleCta: 'Charge a room night', saleTitle: 'Take payment for your first room night',
                        completeCta: 'Receive room payment', badge: 'ROOM PAYMENT RECEIVED',
                        heading: `Your first room night is paid on ${this.$platformBrand.name}.`,
                        nudge: { icon: 'bi-calendar2-check', text: 'Next: open Reservations to take bookings with check-in and check-out dates, and see which rooms are free.' },
                    };
                }
                if (mods.includes('has_kds')) {
                    return {
                        productLabel: 'menu item', productPlaceholder: 'e.g. Chicken & Chips',
                        addedLabel: 'is on your menu.', addedSubtext: "Now let's take your first order.",
                        saleCta: 'Take first order', saleTitle: 'Take your first order',
                        completeCta: 'Complete order', badge: 'ORDER COMPLETE',
                        heading: `Your first order is in. ${this.$platformBrand.name} is live.`,
                        nudge: { icon: 'bi-display', text: 'Kitchen Display is on — orders you send to the kitchen show up on the kitchen screen for your cooks.' },
                    };
                }
                if (mods.includes('has_subscriptions') && type === 'fitness') {
                    return {
                        productLabel: 'membership fee', productPlaceholder: 'e.g. Day Pass or Monthly Fee',
                        addedLabel: 'is ready to collect.', addedSubtext: "Now let's collect your first membership fee.",
                        saleCta: 'Collect first fee', saleTitle: 'Collect your first membership fee',
                        completeCta: 'Receive fee', badge: 'MEMBERSHIP FEE RECEIVED',
                        heading: `Your first membership fee is in on ${this.$platformBrand.name}.`,
                        nudge: { icon: 'bi-people-fill', text: 'Next: open Subscription to register members, so you can track their days left, check-ins and renewals.' },
                    };
                }
                if (type === 'services' || mods.includes('has_appointments')) {
                    return {
                        productLabel: 'service', productPlaceholder: 'e.g. Ladies Haircut',
                        addedLabel: 'is ready to offer.', addedSubtext: "Now let's get paid for your first service.",
                        saleCta: 'Get paid for a service', saleTitle: 'Get paid for your first service',
                        completeCta: 'Receive payment', badge: 'SERVICE PAID',
                        heading: `Your first service is paid on ${this.$platformBrand.name}.`,
                        nudge: null,
                    };
                }
                if (mods.includes('has_manufacturing')) {
                    return {
                        productLabel: 'product', productPlaceholder: 'e.g. Bread Loaf 400g',
                        addedLabel: 'is in your product list.', addedSubtext: "Now let's sell your first finished product.",
                        saleCta: 'Sell a finished product', saleTitle: 'Sell your first finished product',
                        completeCta: 'Complete sale', badge: 'FIRST PRODUCT SOLD',
                        heading: `Your first finished product is sold on ${this.$platformBrand.name}.`,
                        nudge: { icon: 'bi-boxes', text: 'Next: open Manufacturing to add your raw materials and recipes, then plan your first production batch.' },
                    };
                }
                return {
                    productLabel: 'product', productPlaceholder: type === 'wholesale' ? 'e.g. Unga Ndovu 2kg (bale)' : 'e.g. Unga Ndovu 2kg',
                    addedLabel: type === 'retail' ? 'is in your shop.' : 'is ready to sell.', addedSubtext: "Now let's make your first sale.",
                    saleCta: 'Make my first sale', saleTitle: 'Make your first sale',
                    completeCta: 'Complete sale', badge: 'SALE COMPLETE',
                    heading: `You just made your first sale on ${this.$platformBrand.name}.`,
                    nudge: null,
                };
            },

            activityCards() {
                const mods    = this.selectedModules;
                const type    = this.selectedBusinessType;
                const hasProd = !!this.createdProduct;

                if (this.isInvoiceOnly) {
                    return [
                        { id: 'brand-documents', icon: 'bi-palette',  primary: true,  action: 'customize-documents',
                          title: 'Customize your invoices & quotes',
                          desc:  'Add your logo, brand color, notes and address — shown on every document.' },
                        { id: 'first-invoice',   icon: 'bi-receipt',  primary: false, action: 'create-invoice',
                          title: 'Create your first invoice',
                          desc:  'Bill a client right away — you can customize documents later.' },
                    ];
                }

                const saleDesc = (context) => hasProd
                    ? context
                    : `Add a ${this.terminology.productLabel} first, then come back to this.`;

                if (mods.includes('has_reservations')) {
                    return [
                        { id: 'add-room',       icon: 'bi-building',        primary: true,  action: 'add-product',
                          title: 'Add your first room type',
                          desc:  'Standard, Deluxe or Suite — just a name and nightly price.' },
                        { id: 'first-booking',  icon: 'bi-cash-coin',       primary: false, action: hasProd ? 'first-sale' : 'add-product',
                          title: 'Take payment for a room night',
                          desc:  saleDesc('Charge a guest for a night. Full bookings with dates are in Reservations.') },
                    ];
                }
                if (mods.includes('has_kds')) {
                    return [
                        { id: 'build-menu',     icon: 'bi-egg-fried',       primary: true,  action: 'add-product',
                          title: 'Build your menu',
                          desc:  'Add your first menu item — name and price to start.' },
                        { id: 'first-order',    icon: 'bi-receipt',         primary: false, action: hasProd ? 'first-sale' : 'add-product',
                          title: 'Take your first order',
                          desc:  saleDesc('Ring up an order and take payment.') },
                    ];
                }
                if (mods.includes('has_subscriptions') && type === 'fitness') {
                    return [
                        { id: 'add-plan',       icon: 'bi-tag',             primary: true,  action: 'add-product',
                          title: 'Set your membership fee',
                          desc:  'Day pass, monthly or annual — just a name and price.' },
                        { id: 'first-member',   icon: 'bi-cart-check',      primary: false, action: hasProd ? 'first-sale' : 'add-product',
                          title: 'Collect your first membership fee',
                          desc:  saleDesc('Take a member\'s payment. Register members under Subscription to track their days.') },
                    ];
                }
                if (type === 'services' || mods.includes('has_appointments')) {
                    return [
                        { id: 'add-service',    icon: 'bi-scissors',        primary: true,  action: 'add-product',
                          title: 'Add your first service',
                          desc:  'Name it and set a price.' },
                        { id: 'first-payment',  icon: 'bi-cash-coin',       primary: false, action: hasProd ? 'first-sale' : 'add-product',
                          title: 'Get paid for a service',
                          desc:  saleDesc('Take payment for a completed service.') },
                    ];
                }
                if (mods.includes('has_manufacturing')) {
                    return [
                        { id: 'add-item',       icon: 'bi-gear-wide-connected', primary: true,  action: 'add-product',
                          title: 'Add a product you make',
                          desc:  'What do you produce? Just a name and selling price.' },
                        { id: 'first-sale-mfg', icon: 'bi-cart-check',          primary: false, action: hasProd ? 'first-sale' : 'add-product',
                          title: 'Sell your first finished product',
                          desc:  saleDesc('Sell what you made. Recipes and production batches are in Manufacturing.') },
                    ];
                }
                // Retail / wholesale / other default
                return [
                    { id: 'add-product',    icon: 'bi-box-seam',        primary: true,  action: 'add-product',
                      title: 'Add your first product',
                      desc:  'Takes 30 seconds — just a name and price.' },
                    { id: 'first-sale-rtl', icon: 'bi-cart-check',      primary: false, action: hasProd ? 'first-sale' : 'add-product',
                      title: 'Make your first sale',
                      desc:  saleDesc('Record a real sale and take payment.') },
                ];
            },

            normalizedSignupPhone() { return this.e164(this.signupPhone); },
            normalizedLoginPhone()  { return this.e164(this.loginPhone); },

            otpCode() { return this.otpDigits.join(''); },

            saleTotal() {
                return this.createdProduct
                    ? parseFloat((this.createdProduct.ppu * this.saleQty).toFixed(2))
                    : 0;
            },

            saleChange() {
                const received = parseFloat(this.saleCashAmount) || 0;
                return Math.max(0, received - this.saleTotal);
            },

            saleCanComplete() {
                if (!this.saleSelectedMethod || this.saleProcessing) return false;
                if (this.saleSelectedMethod.payment_method === 'Cash') {
                    return (parseFloat(this.saleCashAmount) || 0) >= this.saleTotal;
                }
                return true;
            },
        },

        watch: {
            step(newStep) {
                if (newStep === 'first-sale')   this.loadFirstSale();
                if (newStep === 'celebration') {
                    this.startCountUp();
                    this.$nextTick(() => this.spawnConfetti());
                }
                if (newStep === 'business-name') {
                    this.$nextTick(() => this.$refs.bizNameInput && this.$refs.bizNameInput.focus());
                }
                if (newStep === 'add-product') {
                    this.$nextTick(() => this.$refs.productNameInput && this.$refs.productNameInput.focus());
                }
                if (newStep === 'otp') {
                    this.startResendTimer();
                    this.$nextTick(() => this.focusOtpBox(0));
                }
                if (newStep === 'signup') {
                    this.$nextTick(() => this.$refs.signupNameInput && this.$refs.signupNameInput.focus());
                }
                if (newStep === 'login-phone') {
                    this.$nextTick(() => this.$refs.loginPhoneInput && this.$refs.loginPhoneInput.focus());
                }
            },
        },

        mounted() {
            this.referralCode = new URLSearchParams(window.location.search).get('ref') || '';
            if (this.referralCode) this.showReferralField = true;
            this.checkAuthAndResume();
            this.resetStuckTimer();
        },

        beforeDestroy() {
            clearInterval(this.resendInterval);
            clearInterval(this.affiliatePollTimer);
            clearTimeout(this.stuckTimer);
        },

        methods: {

            // ── Navigation ────────────────────────────────────────────

            navigate(step, dir = 'forward') {
                this.direction = dir;
                this.step      = step;
                this.error     = null;
                this.errors    = {};
                this.trackStep(step);
                this.resetStuckTimer();
            },

            // Silent — fire-and-forget. Never blocks navigation on failure.
            trackStep(step) {
                axios.post('/api/onboarding-step', { step, context: this.onboardingContext() }).catch(() => {});
            },

            // Snapshot of the choices made so far - lets the Slack-visible
            // journey say *what* they picked (business type, use case,
            // modules), not just *which screen* they were on.
            onboardingContext() {
                const context = {};
                if (this.businessName) context.business_name = this.businessName;
                if (this.selectedBusinessType) context.business_type = this.selectedBusinessType;
                if (this.primaryUseCase) context.primary_use_case = this.primaryUseCase;
                if (this.selectedModules.length) context.modules = this.selectedModules;
                return context;
            },

            resetStuckTimer() {
                clearTimeout(this.stuckTimer);
                this.showStuckNudge = false;
                this.showSpotlight  = false;
                this.stuckTimer = setTimeout(() => { this.showStuckNudge = true; }, 45000);
            },

            dismissStuckNudge() {
                this.showStuckNudge = false;
                clearTimeout(this.stuckTimer);
            },

            showNextClickSpotlight() {
                this.showStuckNudge = false;
                this.$nextTick(() => {
                    // One step is ever rendered at a time, so the last
                    // primary/submit button in the active DOM subtree is a
                    // reliable stand-in for "the next action" without having
                    // to tag all 11 step templates individually.
                    const candidates = this.$el.querySelectorAll('.btn-primary, button[type="submit"]');
                    const target = candidates[candidates.length - 1];
                    if (!target) return;

                    // Several steps (document-branding especially) are long
                    // enough that the target can be scrolled out of view —
                    // positioning the spotlight at its current (off-screen)
                    // rect just dims the screen with no visible highlight.
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => this.positionSpotlightOn(target), 350);
                });
            },

            positionSpotlightOn(target) {
                const r = target.getBoundingClientRect();
                this.spotlightStyle = {
                    top:    (r.top - 8) + 'px',
                    left:   (r.left - 8) + 'px',
                    width:  (r.width + 16) + 'px',
                    height: (r.height + 16) + 'px',
                };
                this.spotlightLabelStyle = {
                    top:  (r.bottom + 12) + 'px',
                    left: r.left + 'px',
                };
                this.showSpotlight = true;
            },

            closeSpotlight() {
                this.showSpotlight = false;
            },

            goBack() {
                const map = {
                    'login-phone':   'signup',
                    'otp':           this.otpContext === 'login' ? 'login-phone' : 'signup',
                    'business-type':    'business-name',
                    'primary-use-case': 'business-type',
                    'modules':          'primary-use-case',
                    'activity-hub':     'modules',
                    'document-branding': 'activity-hub',
                    'add-product':   'activity-hub',
                    'product-added': 'activity-hub',
                    'first-sale':    'product-added',
                };
                const prev = map[this.step];
                if (prev) this.navigate(prev, 'back');
            },

            // ── Auth detection & resume ───────────────────────────────

            async checkAuthAndResume() {
                try {
                    const res = await axios.get('/api/onboarding-state');
                    this.handleAuthenticatedState(res.data);
                    this.checkAffiliation();
                } catch (e) {
                    // 401 = not authenticated → show signup
                    this.navigate('signup');
                }
            },

            handleAuthenticatedState(s) {
                if (s.onboard_done) {
                    window.location.replace('/app');
                    return;
                }

                // Sync affiliate state (for personalised copy on payment wall)
                this.hasAffiliate       = !!s.has_affiliate;
                this.affiliateIsDukakit = !!s.affiliate_is_dukakit;
                this.affiliateName      = s.affiliate_name || '';
                this.onboardingFeePaid  = !!s.onboarding_fee_paid;

                if (s.primary_type) {
                    const mapped = LEGACY_INDUSTRY_MAP[s.primary_type] || s.primary_type;
                    if (BUSINESS_TYPES.find(t => t.id === mapped)) {
                        this.selectedBusinessType = mapped;
                    }
                }
                if (s.active_modules && s.active_modules.length > 0) {
                    this.selectedModules = [...s.active_modules];
                } else if (this.selectedBusinessType) {
                    const type = BUSINESS_TYPES.find(t => t.id === this.selectedBusinessType);
                    if (type) this.selectedModules = [...type.defaultModules];
                }
                this.primaryUseCase = s.primary_use_case || null;

                // Credentials not set yet and user hasn't progressed past business name → show early prompt
                if (!s.credentials_set && !s.business_profile_saved) {
                    this.credentialsIsEarlyStep = true;
                    this.navigate('credentials');
                    return;
                }

                // Onboarding fee gate — only applies when the company was onboarded via an affiliate
                if (s.has_affiliate && !s.onboarding_fee_paid && !s.business_profile_saved) {
                    this.navigate('affiliate-payment');
                    return;
                }

                if (s.last_product) {
                    this.createdProduct     = s.last_product;
                    this.createdProductName = s.last_product.product;
                    this.navigate('activity-hub');
                } else if (s.business_profile_saved) {
                    this.navigate('activity-hub');
                } else {
                    this.navigate('business-name');
                }
            },

            async checkAffiliation() {
                // affiliate status is now returned directly in onboardingState
                // kept for compatibility with post-product-added note check
                try {
                    const res         = await axios.get('/api/company-affiliate-status');
                    this.hasAffiliate = !!res.data;
                } catch (_) {}
            },

            // ── Signup ────────────────────────────────────────────────

            async submitSignup() {
                this.errors = {};
                this.error  = null;
                if (!this.signupName.trim()) {
                    this.errors.name = 'Enter your name.';
                    return;
                }
                if (!this.isPhoneValid(this.signupPhone)) {
                    this.errors.phone = 'Enter a valid Kenya phone number.';
                    return;
                }
                if (!this.termsAccepted) {
                    this.error = 'Please accept the Terms of Service to continue.';
                    return;
                }
                this.isLoading = true;
                try {
                    await axios.post('/api/register-phone', {
                        name:         this.signupName.trim(),
                        phone:        this.normalizedSignupPhone,
                        referralCode: this.referralCode,
                    });
                    this.activePhone  = this.normalizedSignupPhone;
                    this.maskedPhone  = this.normalizedSignupPhone.slice(-4);
                    this.otpContext   = 'signup';
                    this.otpDigits    = ['', '', '', '', '', ''];
                    this.navigate('otp');
                } catch (err) {
                    const msg = err.response?.data?.message || '';
                    if (msg.toLowerCase().includes('phone') || msg.toLowerCase().includes('taken')) {
                        this.errors.phone = 'This number already has an account.';
                    } else {
                        this.error = msg || 'Something went wrong. Please try again.';
                    }
                } finally {
                    this.isLoading = false;
                }
            },

            // ── Login ─────────────────────────────────────────────────

            async submitLoginPhone() {
                this.errors = {};
                this.error  = null;
                if (!this.isPhoneValid(this.loginPhone)) {
                    this.errors.phone = 'Enter a valid Kenya phone number.';
                    return;
                }
                this.isLoading = true;
                try {
                    await axios.post('/api/request-login-otp', { phone: this.normalizedLoginPhone });
                    this.activePhone = this.normalizedLoginPhone;
                    this.maskedPhone = this.normalizedLoginPhone.slice(-4);
                    this.otpContext  = 'login';
                    this.otpDigits   = ['', '', '', '', '', ''];
                    this.navigate('otp');
                } catch (err) {
                    this.error = err.response?.data?.message || 'No account found with that number.';
                } finally {
                    this.isLoading = false;
                }
            },

            // ── OTP ───────────────────────────────────────────────────

            onOtpDigit(i, e) {
                const raw   = e.target.value.replace(/\D/g, '');
                const digit = raw ? raw[raw.length - 1] : '';
                this.$set(this.otpDigits, i, digit);
                if (digit && i < 5) {
                    this.$nextTick(() => this.focusOtpBox(i + 1));
                }
                this.$nextTick(() => {
                    if (this.otpDigits.every(d => d !== '')) this.submitOtp();
                });
            },

            onOtpKey(i, e) {
                if (e.key === 'Backspace') {
                    if (this.otpDigits[i]) {
                        this.$set(this.otpDigits, i, '');
                    } else if (i > 0) {
                        this.$set(this.otpDigits, i - 1, '');
                        this.$nextTick(() => this.focusOtpBox(i - 1));
                    }
                } else if (e.key === 'ArrowLeft'  && i > 0) this.focusOtpBox(i - 1);
                  else if (e.key === 'ArrowRight' && i < 5) this.focusOtpBox(i + 1);
            },

            onOtpPaste(e) {
                const text = (e.clipboardData || window.clipboardData)
                    .getData('text').replace(/\D/g, '').slice(0, 6);
                text.split('').forEach((c, i) => this.$set(this.otpDigits, i, c));
                const focusIdx = Math.min(text.length, 5);
                this.$nextTick(() => {
                    this.focusOtpBox(focusIdx);
                    if (this.otpDigits.every(d => d !== '')) this.submitOtp();
                });
            },

            focusOtpBox(i) {
                const ref = this.$refs['otp' + i];
                const el  = Array.isArray(ref) ? ref[0] : ref;
                el && el.focus();
            },

            async submitOtp() {
                if (this.otpCode.length < 6 || this.isLoading) return;
                this.isLoading = true;
                this.error     = null;
                try {
                    const res = await axios.post('/api/verify-otp', {
                        phone: this.activePhone,
                        otp:   this.otpCode,
                    });
                    if (res.data.onboard_done) {
                        window.location.replace('/app');
                        return;
                    }
                    // Now authenticated — get current state
                    const stateRes = await axios.get('/api/onboarding-state');
                    const state    = stateRes.data;

                    // New signup: credentials not yet set → prompt now before business setup
                    if (this.otpContext === 'signup' && !state.credentials_set) {
                        this.credentialsIsEarlyStep = true;
                        this.onboardingFeePaid      = !!state.onboarding_fee_paid;
                        this.hasAffiliate           = !!state.has_affiliate;
                        this.affiliateIsDukakit     = !!state.affiliate_is_dukakit;
                        this.affiliateName          = state.affiliate_name || '';
                        this.navigate('credentials');
                        return;
                    }

                    this.handleAuthenticatedState(state);
                } catch (err) {
                    this.otpDigits = ['', '', '', '', '', ''];
                    this.error     = err.response?.data?.message || 'Invalid code. Please try again.';
                    this.$nextTick(() => this.focusOtpBox(0));
                } finally {
                    this.isLoading = false;
                }
            },

            async resendOtp() {
                this.otpSuccess = '';
                this.error      = '';
                try {
                    await axios.post('/api/resend-otp', { phone: this.activePhone });
                    this.otpSuccess   = 'A new code has been sent.';
                    this.resendSeconds = 60;
                    this.startResendTimer();
                } catch (_) {
                    this.error = 'Could not resend. Please try again.';
                }
            },

            startResendTimer() {
                clearInterval(this.resendInterval);
                this.resendSeconds = 60;
                this.resendInterval = setInterval(() => {
                    if (this.resendSeconds > 0) {
                        this.resendSeconds--;
                    } else {
                        clearInterval(this.resendInterval);
                    }
                }, 1000);
            },

            // ── Business name ─────────────────────────────────────────

            submitBusinessName() {
                if (!this.businessName.trim()) {
                    this.errors.businessName = 'Enter your business name.';
                    return;
                }
                this.errors = {};
                this.navigate('business-type');
            },

            // ── Business type ─────────────────────────────────────────

            selectType(typeId) {
                this.selectedBusinessType = typeId;
                const type = BUSINESS_TYPES.find(t => t.id === typeId);
                if (type) this.selectedModules = [...type.defaultModules];
                setTimeout(() => this.navigate('primary-use-case'), 320);
            },

            // ── Primary use case ──────────────────────────────────────

            selectPrimaryUseCase(value) {
                this.primaryUseCase = value;
                setTimeout(() => this.navigate('modules'), 320);
            },

            // ── Modules ───────────────────────────────────────────────

            toggleModule(flag) {
                const idx = this.selectedModules.indexOf(flag);
                if (idx === -1) {
                    this.selectedModules.push(flag);
                } else {
                    this.selectedModules.splice(idx, 1);
                }
            },

            async confirmModules() {
                this.isLoading = true;
                this.error     = null;
                try {
                    await axios.post('/api/update-business-profile', {
                        name:          this.businessName || undefined,
                        businessType:  this.selectedBusinessType,
                        modules:       this.selectedModules,
                        primaryUseCase: this.primaryUseCase,
                    });
                    this.navigate('activity-hub');
                } catch (_) {
                    this.error = 'Could not save your settings. Please try again.';
                } finally {
                    this.isLoading = false;
                }
            },

            // ── Activity hub ──────────────────────────────────────────

            async handleActivity(card) {
                this.error = null;
                if (card.action === 'add-product') {
                    this.navigate('add-product');
                } else if (card.action === 'first-sale') {
                    this.isLoading = true;
                    try {
                        await this.completeSetup();
                        this.navigate('first-sale');
                    } catch (_) {
                        this.error = 'Setup failed. Please try again.';
                    } finally {
                        this.isLoading = false;
                    }
                } else if (card.action === 'customize-documents') {
                    this.navigate('document-branding');
                    this.loadDocumentSettings();
                } else if (card.action === 'create-invoice') {
                    await this.finishAndCreateDocument('/invoices/create');
                }
            },

            // ── Document branding (invoice-only) ───────────────────────

            async loadDocumentSettings() {
                if (this.documentSettings) return;
                this.documentSettingsLoading = true;
                try {
                    const { data } = await axios.get('/api/user');
                    this.documentSettings = data.company;
                } catch (_) {
                    this.error = 'Could not load your business details. Please try again.';
                } finally {
                    this.documentSettingsLoading = false;
                }
            },

            openDocPreview(type) {
                this.docPreviewType  = type;
                this.showDocPreview  = true;
            },

            async uploadLogo(e) {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > 2 * 1024 * 1024) {
                    this.error = 'Logo must be under 2MB.';
                    e.target.value = '';
                    return;
                }

                this.logoUploading = true;
                this.error = null;
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const uploadRes = await axios.post('/api/files', formData);

                    const { data } = await axios.post('/api/update-company-logo', {
                        companyLogo: uploadRes.data.file,
                    });
                    this.documentSettings.logo_url = data.url;
                } catch (_) {
                    this.error = 'Could not upload your logo. Please try again.';
                } finally {
                    this.logoUploading = false;
                    e.target.value = '';
                }
            },

            async saveDocumentSettings() {
                if (!this.documentSettings) return true;
                this.documentSettingsSaving = true;
                try {
                    await axios.post('/api/update-company', { ...this.documentSettings });
                    return true;
                } catch (_) {
                    this.error = 'Could not save your document settings. Please try again.';
                    return false;
                } finally {
                    this.documentSettingsSaving = false;
                }
            },

            async continueFromDocumentBranding() {
                const saved = await this.saveDocumentSettings();
                if (saved) this.navigate('activity-hub');
            },

            async finishAndCreateDocument(destination) {
                this.isLoading = true;
                try {
                    if (this.step === 'document-branding') {
                        const saved = await this.saveDocumentSettings();
                        if (!saved) return;
                    }
                    await this.completeSetup();
                    window.location.href = destination;
                } catch (_) {
                    this.error = 'Setup failed. Please try again.';
                } finally {
                    this.isLoading = false;
                }
            },

            // ── Product ───────────────────────────────────────────────

            async submitProduct() {
                if (!this.product.name || !this.product.price) return;
                this.isLoading = true;
                this.error     = null;
                try {
                    const mods = this.selectedModules;
                    // Onboarding never collects a starting stock count, so this item
                    // can't be inventory-tracked yet — the merchant sets that up later
                    // from the full product form. Service-type businesses (bookings,
                    // rooms, memberships) don't carry stock at all, so flag them as such.
                    const isServiceType = mods.includes('has_appointments')
                        || this.selectedBusinessType === 'services'
                        || mods.includes('has_reservations')
                        || (mods.includes('has_subscriptions') && this.selectedBusinessType === 'fitness');

                    const res = await axios.post('/api/addprod', {
                        title:             this.product.name,
                        ppu:               parseFloat(this.product.price),
                        can_be_sold:       true,
                        is_Service:        isServiceType,
                        affects_inventory: false,
                    });
                    this.createdProduct     = res.data.product;
                    this.createdProductName = res.data.product.product;
                    this.navigate('product-added');
                } catch (_) {
                    this.error = `Could not add your ${this.terminology.productLabel}. Please try again.`;
                } finally {
                    this.isLoading = false;
                }
            },

            // ── Product added → first sale ────────────────────────────

            async startFirstSale() {
                this.error     = null;
                this.isLoading = true;
                try {
                    await this.completeSetup();
                    this.navigate('first-sale');
                } catch (_) {
                    this.error = 'Setup failed. Please try again.';
                } finally {
                    this.isLoading = false;
                }
            },

            async skipToShop() {
                this.error     = null;
                const fromStep = this.step;
                this.navigate('finishing');
                this.finishingMessage = `Setting up your ${this.businessNoun}…`;
                try {
                    await this.completeSetup();
                    setTimeout(() => window.location.replace('/app'), 1200);
                } catch (_) {
                    this.navigate(fromStep);
                    this.error = 'Setup failed. Please try again.';
                }
            },

            async completeSetup() {
                if (this.setupCompleted) return;
                await axios.post('/api/complete-onboarding', { payments: ['Cash', 'M-pesa'] });
                this.setupCompleted = true;
            },

            // ── First sale ────────────────────────────────────────────

            async loadFirstSale() {
                this.saleLoadingPayments = true;
                this.saleQty             = 1;
                this.saleCashAmount      = '';
                this.saleSelectedMethod  = null;
                try {
                    const res   = await axios.get('/api/payments');
                    const types = (Array.isArray(res.data) ? res.data : []).filter(p => p.enabled);
                    this.salePaymentTypes = types;
                    if (types.length > 0) {
                        this.saleSelectedMethod = types.find(p => p.payment_method === 'Cash') || types[0];
                    }
                } catch (_) {}
                finally { this.saleLoadingPayments = false; }
            },

            async completeSale() {
                if (!this.saleCanComplete) return;
                this.saleProcessing = true;
                this.error          = null;
                const total    = this.saleTotal;
                const cartItem = {
                    id:           this.createdProduct.id,
                    product:      this.createdProduct.product,
                    ppu:          this.createdProduct.ppu,
                    wholesale_sp: this.createdProduct.ppu,
                    quantity:     this.saleQty,
                    totalPrice:   total,
                    is_Retail:    true,
                    is_service:   false,
                };
                try {
                    const orderRes = await axios.post('/api/order', {
                        cart:         JSON.stringify([cartItem]),
                        customerdata: JSON.stringify(0),
                        totalprice:   JSON.stringify(total),
                        discount:     0,
                    });
                    await axios.post('/api/full-payment', {
                        paymentMethod: this.saleSelectedMethod.id,
                        amount:        total,
                        orderId:       orderRes.data.id,
                    });
                    this.saleCompletedAmount = total;
                    this.saleDisplayAmount   = 0;
                    this.saleDoneLabel       = this.buildSaleDoneLabel();
                    this.navigate('celebration');
                } catch (_) {
                    this.error = 'Could not complete. Please try again.';
                } finally {
                    this.saleProcessing = false;
                }
            },

            buildSaleDoneLabel() {
                const now  = new Date();
                const time = now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
                const date = now.toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short' });
                return `${date}, ${time}`;
            },

            // ── Celebration ───────────────────────────────────────────

            startCountUp() {
                const target   = this.saleCompletedAmount;
                const steps    = 60;
                const delay    = 1200 / steps;
                let   current  = 0;
                const inc      = target / steps;
                const timer    = setInterval(() => {
                    current                  = Math.min(current + inc, target);
                    this.saleDisplayAmount   = current;
                    if (current >= target) clearInterval(timer);
                }, delay);
            },

            spawnConfetti() {
                const colors = ['#fd4719', '#212529', '#adb5bd', '#6c757d', '#fff'];
                for (let i = 0; i < 52; i++) {
                    setTimeout(() => {
                        const el = document.createElement('div');
                        el.style.cssText = [
                            'position:fixed',
                            'top:-12px',
                            `left:${Math.random() * 100}vw`,
                            `width:${6 + Math.random() * 6}px`,
                            `height:${6 + Math.random() * 6}px`,
                            `background:${colors[Math.floor(Math.random() * colors.length)]}`,
                            `border-radius:${Math.random() > 0.5 ? '50%' : '2px'}`,
                            `animation:ob-confetti-fall ${2 + Math.random() * 2}s ease-in forwards`,
                            `animation-delay:${Math.random() * 0.8}s`,
                            'z-index:9999',
                            'pointer-events:none',
                        ].join(';');
                        document.body.appendChild(el);
                        setTimeout(() => el.parentNode && el.parentNode.removeChild(el), 4500);
                    }, i * 28);
                }
            },

            shareOnWhatsApp() {
                const text = [
                    this.terminology.badge,
                    '',
                    `KES ${this.formatAmount(this.saleCompletedAmount)}`,
                    `${this.createdProductName} · ${this.saleDoneLabel}`,
                    '',
                    `Powered by ${this.$platformBrand.name}`,
                ].join('\n');
                window.open('https://wa.me/?text=' + encodeURIComponent(text), '_blank');
            },

            // ── Affiliate payment ─────────────────────────────────────

            async initiateAffiliatePay() {
                this.affiliatePayLoading = true;
                this.affiliatePayError   = '';
                try {
                    const res = await axios.post('/api/pay-onboarding-fee', {
                        mpesaPhoneNumber:   this.e164(this.affiliatePhone),
                        onboardingPackages: this.effectiveFeeAmount,
                    });
                    this.affiliateBillingId     = res.data.id;
                    this.affiliateStkSent       = true;
                    this.affiliatePollCountdown = 180;
                    this.startAffiliatePoll();
                } catch (err) {
                    this.affiliatePayError = err.response?.data?.message || 'Could not initiate payment. Please try again.';
                } finally {
                    this.affiliatePayLoading = false;
                }
            },

            retryAffiliatePay() {
                clearInterval(this.affiliatePollTimer);
                this.affiliatePollTimer = null;
                this.affiliateStkSent   = false;
                this.affiliatePayError  = '';
            },

            startAffiliatePoll() {
                this.affiliatePollTimer = setInterval(async () => {
                    this.affiliatePollCountdown = Math.max(0, this.affiliatePollCountdown - 5);
                    try {
                        const res = await axios.get('/api/validate-mpesa-onboarding', {
                            params: { bill_id: this.affiliateBillingId },
                        });
                        if (res.data.message === 1) {
                            clearInterval(this.affiliatePollTimer);
                            this.affiliatePollTimer = null;
                            this.onboardingFeePaid  = true;
                            this.navigate('business-name');
                            return;
                        }
                        if (res.data.message === 0) {
                            clearInterval(this.affiliatePollTimer);
                            this.affiliatePollTimer = null;
                            this.affiliatePayError  = res.data.description || 'Payment was declined. Please try again.';
                            this.affiliateStkSent   = false;
                            return;
                        }
                    } catch (_) {}
                    if (this.affiliatePollCountdown <= 0) {
                        clearInterval(this.affiliatePollTimer);
                        this.affiliatePollTimer = null;
                        this.affiliateStkSent   = false;
                        this.affiliatePayError  = 'Payment timed out. Please try again.';
                    }
                }, 5000);
            },

            // ── Credentials ───────────────────────────────────────────

            async saveCredentials() {
                this.credentialsError   = '';
                this.credentialsSuccess = '';
                if (!this.credentials.email.trim()) {
                    this.credentialsError = 'Enter your email address.';
                    return;
                }
                if (this.credentials.password.length < 8) {
                    this.credentialsError = 'Password must be at least 8 characters.';
                    return;
                }
                this.credentialsLoading = true;
                try {
                    await axios.post('/api/set-credentials', {
                        email:    this.credentials.email,
                        password: this.credentials.password,
                    });
                    if (this.credentialsIsEarlyStep) {
                        this.credentialsIsEarlyStep = false;
                        this.credentialsSuccess = 'Saved!';
                        const needsFee = this.hasAffiliate && !this.onboardingFeePaid;
                        setTimeout(() => this.navigate(needsFee ? 'affiliate-payment' : 'business-name'), 700);
                    } else {
                        this.credentialsSuccess = `Done! Opening your ${this.businessNoun}…`;
                        setTimeout(() => this.goToShop(), 900);
                    }
                } catch (err) {
                    this.credentialsError = err.response?.data?.message || 'Could not save. Please try again.';
                } finally {
                    this.credentialsLoading = false;
                }
            },

            skipCredentials() {
                if (this.credentialsIsEarlyStep) {
                    this.credentialsIsEarlyStep = false;
                    const needsFee = this.hasAffiliate && !this.onboardingFeePaid;
                    this.navigate(needsFee ? 'affiliate-payment' : 'business-name');
                } else {
                    this.goToShop();
                }
            },

            // ── Utilities ─────────────────────────────────────────────

            goToShop() {
                this.navigate('finishing');
                this.finishingMessage = `Opening your ${this.businessNoun}…`;
                setTimeout(() => window.location.replace('/app'), 1200);
            },

            e164(raw) {
                const digits = (raw || '').replace(/\D/g, '');
                const local  = digits.startsWith('0') ? digits.slice(1) : digits;
                return '+254' + local;
            },

            isPhoneValid(raw) {
                const digits = (raw || '').replace(/\D/g, '');
                return (digits.length === 10 && (digits.startsWith('07') || digits.startsWith('01'))) ||
                       (digits.length === 9  && (digits.startsWith('7')  || digits.startsWith('1')));
            },

            formatAmount(value) {
                return parseFloat(value || 0).toLocaleString('en-KE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            },
        },
    };
</script>

<style scoped>
/* ── Shell ──────────────────────────────────────────────────────────── */
.ob-shell {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1.25rem 2rem;
    background: #fff;
}

.ob-shell--celebration {
    background: radial-gradient(ellipse at 50% 0%, rgba(253, 71, 25, 0.05) 0%, #fff 55%);
}

.ob-inner {
    width: 100%;
    max-width: 420px;
}

/* ── Stuck nudge ────────────────────────────────────────────────────── */
.ob-stuck-nudge {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1200;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    padding: 14px 18px;
    max-width: 280px;
}

.ob-stuck-nudge__close {
    position: absolute;
    top: 6px;
    right: 10px;
    border: none;
    background: none;
    font-size: 1.1rem;
    line-height: 1;
    color: #999;
}

/* ── Spotlight overlay ─────────────────────────────────────────────── */
.ob-spotlight-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1300;
}

.ob-spotlight-hole {
    position: fixed;
    border-radius: 8px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.65);
    pointer-events: none;
    transition: all 0.2s ease;
}

.ob-spotlight-label {
    position: fixed;
    color: #fff;
    font-weight: 600;
    margin: 0;
    pointer-events: none;
}

/* ── Progress bar ───────────────────────────────────────────────────── */
.ob-progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    height: 3px;
    background: #fd4719;
    transition: width 0.45s ease;
    z-index: 200;
}

/* ── Back button ────────────────────────────────────────────────────── */
.ob-back {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #6c757d;
    font-size: 0.875rem;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    margin-bottom: 1.75rem;
    transition: color 0.15s ease;
}
.ob-back:hover { color: #212529; }

/* ── Brand mark ─────────────────────────────────────────────────────── */
.ob-brand-mark {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #fd4719;
    margin-bottom: 1rem;
}

/* ── Typography ─────────────────────────────────────────────────────── */
.ob-heading {
    font-size: 1.6rem;
    font-weight: 700;
    color: #212529;
    line-height: 1.2;
    margin-bottom: 0.5rem;
}

.ob-subtext {
    color: #6c757d;
    font-size: 0.9rem;
    margin-bottom: 2rem;
}

.ob-logo-preview {
    width: 56px;
    height: 56px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    color: #adb5bd;
    font-size: 1.4rem;
}

.ob-logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.ob-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6c757d;
    margin-bottom: 0.4rem;
    display: block;
}

/* ── Inputs ─────────────────────────────────────────────────────────── */
.ob-input {
    border: none !important;
    border-bottom: 2px solid #dee2e6 !important;
    border-radius: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    background: transparent !important;
    font-size: 1.1rem;
    color: #212529;
    box-shadow: none !important;
    transition: border-color 0.15s ease;
}
.ob-input:focus {
    border-bottom-color: #fd4719 !important;
    background: transparent !important;
    outline: none !important;
}

/* Phone row */
.ob-phone-row {
    display: flex;
    align-items: flex-end;
    border-bottom: 2px solid #dee2e6;
    transition: border-color 0.15s ease;
    gap: 6px;
}
.ob-phone-row--focus { border-bottom-color: #fd4719; }

.ob-phone-cc {
    font-size: 0.9rem;
    color: #6c757d;
    padding-bottom: 0.45rem;
    white-space: nowrap;
    flex-shrink: 0;
}

.ob-phone-input {
    flex: 1;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0.45rem 0;
    font-size: 1.1rem;
    color: #212529;
    background: transparent;
    min-width: 0;
}
.ob-phone-input::placeholder { color: #adb5bd; }

/* Password row */
.ob-pw-row {
    display: flex;
    align-items: flex-end;
    border-bottom: 2px solid #dee2e6;
    transition: border-color 0.15s ease;
}
.ob-pw-row:focus-within { border-bottom-color: #fd4719; }

.ob-pw-input {
    border-bottom: none !important;
    flex: 1;
}

.ob-pw-toggle {
    border: none;
    background: transparent;
    color: #6c757d;
    padding: 0 0 0.45rem 0.5rem;
    cursor: pointer;
    flex-shrink: 0;
}
.ob-pw-toggle:focus { outline: none; }

/* ── Terms checkbox ─────────────────────────────────────────────────── */
.ob-checkbox {
    width: 16px;
    height: 16px;
    margin-top: 3px;
    accent-color: #fd4719;
    cursor: pointer;
}
.ob-terms-label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-left: 4px;
    cursor: pointer;
    line-height: 1.4;
}
.ob-link { color: #212529; font-weight: 600; }

/* ── Buttons ────────────────────────────────────────────────────────── */
.ob-cta {
    padding: 0.825rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 6px;
}
.ob-cta-secondary {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 6px;
}
.ob-skip {
    font-size: 0.875rem;
    text-decoration: none;
    color: #6c757d !important;
}
.ob-inline-link {
    font-size: inherit;
    color: #212529 !important;
    font-weight: 600;
    vertical-align: baseline;
    text-decoration: underline;
}
.ob-footer-link {
    font-size: 0.875rem;
    color: #6c757d;
    text-align: center;
    margin-top: 1rem;
}

/* ── OTP boxes ──────────────────────────────────────────────────────── */
.ob-otp-phone-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    color: #212529;
    margin: 0 auto;
}

.ob-otp-boxes {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin: 1.75rem 0;
}

.ob-otp-box {
    width: 48px;
    height: 56px;
    text-align: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: #212529;
    background: #f8f9fa;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.15s ease, background 0.15s ease;
    caret-color: transparent;
    -webkit-appearance: none;
    appearance: none;
}
.ob-otp-box:focus           { border-color: #fd4719 !important; background: #fff; }
.ob-otp-box--filled         { background: #fff; border-color: #212529; }
.ob-otp-box--loading        { opacity: 0.5; pointer-events: none; }

.ob-resend-row {
    font-size: 0.875rem;
    color: #6c757d;
    text-align: center;
    margin-top: 1rem;
}

/* ── Business type grid ─────────────────────────────────────────────── */
.ob-type-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.ob-type-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 1.1rem 1rem 0.9rem;
    border: 2px solid #dee2e6;
    border-radius: 10px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    user-select: none;
    min-height: 100px;
}
.ob-type-tile:hover {
    border-color: #adb5bd;
    transform: translateY(-1px);
}
.ob-type-tile--selected {
    border-color: #fd4719;
    box-shadow: 0 0 0 3px rgba(253, 71, 25, 0.1);
    animation: ob-tile-pulse 0.3s ease;
}

@keyframes ob-tile-pulse {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.025); }
    100% { transform: scale(1); }
}

.ob-type-tile__icon {
    font-size: 1.5rem;
    color: #6c757d;
    margin-bottom: 0.6rem;
    display: block;
    transition: color 0.15s ease;
}
.ob-type-tile--selected .ob-type-tile__icon { color: #fd4719; }

.ob-type-tile__name {
    font-size: 0.82rem;
    font-weight: 700;
    color: #212529;
    line-height: 1.2;
}
.ob-type-tile__sub {
    font-size: 0.7rem;
    color: #6c757d;
    margin-top: 1px;
}
.ob-type-tile__check {
    position: absolute;
    top: 8px;
    right: 9px;
    font-size: 0.85rem;
    color: #fd4719;
}

/* ── Core modules (locked) ──────────────────────────────────────────── */
.ob-core-list { display: flex; flex-direction: column; gap: 6px; }
.ob-core-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    background: #f8f9fa;
    border-radius: 7px;
}
.ob-core-item__icon  { font-size: 0.95rem; color: #adb5bd; flex-shrink: 0; }
.ob-core-item__label { font-size: 0.83rem; color: #6c757d; flex: 1; }
.ob-core-item__badge {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #adb5bd;
}

/* ── Optional modules ───────────────────────────────────────────────── */
.ob-module-list { display: flex; flex-direction: column; gap: 8px; }

.ob-module {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 14px;
    border: 1.5px solid #dee2e6;
    border-radius: 8px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.15s ease, background 0.15s ease;
    user-select: none;
}
.ob-module:hover    { border-color: #adb5bd; }
.ob-module--on      { border-color: #fd4719; background: #fff9f8; }

.ob-module__left    { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.ob-module__icon    { font-size: 1.2rem; color: #6c757d; flex-shrink: 0; transition: color 0.15s ease; }
.ob-module--on .ob-module__icon { color: #fd4719; }
.ob-module__text    { min-width: 0; }
.ob-module__title   { font-size: 0.875rem; font-weight: 600; color: #212529; line-height: 1.3; }
.ob-module__desc    { font-size: 0.72rem; color: #6c757d; line-height: 1.3; }

.ob-module__check {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1.5px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 10px;
    color: transparent;
    font-size: 13px;
    transition: all 0.15s ease;
}
.ob-module--on .ob-module__check {
    background: #fd4719;
    border-color: #fd4719;
    color: #fff;
}

/* ── Options (payment methods) ──────────────────────────────────────── */
.ob-option-list { display: flex; flex-direction: column; gap: 8px; }

.ob-option {
    display: flex;
    align-items: center;
    padding: 13px 14px;
    border: 1.5px solid #dee2e6;
    border-radius: 6px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.15s ease;
    user-select: none;
    margin: 0;
}
.ob-option--on { border-color: #fd4719; }

.ob-option__radio {
    width: 22px;
    height: 22px;
    border: 1.5px solid #adb5bd;
    border-radius: 50%;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: transparent;
    font-size: 13px;
    transition: all 0.15s ease;
}
.ob-option__radio--on {
    background: #fd4719;
    border-color: #fd4719;
    color: #fff;
}
.ob-option__label { font-size: 1rem; color: #212529; }

/* ── Sale card ──────────────────────────────────────────────────────── */
.ob-sale-card {
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    padding: 1rem 0;
}
.ob-sale-card__name  { font-size: 1rem; font-weight: 600; color: #212529; }
.ob-sale-card__price { font-size: 0.85rem; color: #6c757d; }
.ob-sale-total       { font-size: 1.15rem; font-weight: 700; color: #212529; }

.ob-qty-row  { display: flex; align-items: center; gap: 10px; }
.ob-qty-btn  {
    width: 36px; height: 36px; border-radius: 4px;
    background: #f8f9fa; border: 1.5px solid #dee2e6;
    font-size: 1.15rem; line-height: 1; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.12s ease;
    flex-shrink: 0;
}
.ob-qty-btn:hover:not(:disabled) { background: #e9ecef; }
.ob-qty-btn:disabled { opacity: 0.4; cursor: default; }
.ob-qty-val  { font-size: 1.25rem; font-weight: 700; min-width: 2rem; text-align: center; }

/* ── Success rings ──────────────────────────────────────────────────── */
.ob-success-ring {
    width: 72px; height: 72px; border-radius: 50%;
    background: #fd4719; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 2.25rem; margin: 0 auto;
}
.ob-success-ring--sm   { width: 56px; height: 56px; font-size: 1.75rem; }
.ob-success-ring--grey { background: #f8f9fa; color: #212529; }

/* ── Product name display ───────────────────────────────────────────── */
.ob-product-name-display {
    font-size: 1.25rem;
    max-width: 100%;
}

/* ── Referral toggle link (signup form) ─────────────────────────────── */
.ob-referral-toggle {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.8rem;
    color: #6c757d;
    cursor: pointer;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
    transition: color 0.15s ease;
}
.ob-referral-toggle:hover { color: #fd4719; }

/* ── Referral highlight banner ──────────────────────────────────────── */
.ob-referral-highlight {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #fff8f6 0%, #fff3ee 100%);
    border: 1.5px solid #fcd5c8;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    color: #212529;
    font-weight: 500;
}

/* ── Affiliate payment wall ─────────────────────────────────────────── */
.ob-affiliate-wall-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: #212529; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.75rem; margin: 0 auto;
}

.ob-affiliate-wait-ring {
    width: 64px; height: 64px;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto;
}

/* ── Affiliate note ─────────────────────────────────────────────────── */
.ob-affiliate-note {
    font-size: 0.85rem; color: #6c757d;
    background: #f8f9fa; border-radius: 6px;
    padding: 10px 14px; text-align: left;
}

/* ── Feature nudge ──────────────────────────────────────────────────── */
.ob-feature-nudge {
    background: #f8f9fa;
    border-left: 3px solid #fd4719;
    border-radius: 0 5px 5px 0;
    padding: 0.65rem 0.9rem;
    font-size: 0.82rem;
    color: #495057;
    text-align: left;
}

/* ── Alerts ─────────────────────────────────────────────────────────── */
.ob-alert {
    border-radius: 6px;
    padding: 0.65rem 1rem;
    font-size: 0.875rem;
}
.ob-alert--error   { background: #fff5f5; border: 1px solid #f5c6cb; color: #842029; }
.ob-alert--success { background: #f0fdf4; border: 1px solid #b7ebc8; color: #166534; }

/* ── Celebration ────────────────────────────────────────────────────── */
.ob-celebration { padding: 1rem 0 0.5rem; }

.ob-celebration-ring {
    width: 96px; height: 96px; border-radius: 50%;
    background: #fd4719; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 2.75rem; margin: 0 auto;
}

.ob-celebration-badge {
    font-size: 0.65rem; font-weight: 700;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: #6c757d; margin: 1.25rem 0 0;
}

.ob-celebration-amount {
    font-size: 3rem; font-weight: 700; color: #212529;
    line-height: 1; margin: 0.2rem 0 0.5rem;
    font-variant-numeric: tabular-nums;
}

/* ── Loading / finishing ────────────────────────────────────────────── */
.ob-loading  { text-align: center; padding: 2rem 0; }
.ob-finishing { padding: 3rem 0; }
.ob-finishing-msg { font-size: 1rem; color: #6c757d; margin-top: 1.25rem; }

/* ── Confetti keyframe ──────────────────────────────────────────────── */
@keyframes ob-confetti-fall {
    0%   { transform: translateY(0) rotate(0deg);    opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}

/* ── Slide transitions ──────────────────────────────────────────────── */
.ob-slide-fwd-enter-active,
.ob-slide-fwd-leave-active,
.ob-slide-bck-enter-active,
.ob-slide-bck-leave-active {
    transition: opacity 0.22s ease, transform 0.22s ease;
}

.ob-slide-fwd-enter      { opacity: 0; transform: translateX(32px); }
.ob-slide-fwd-leave-to   { opacity: 0; transform: translateX(-32px); }

.ob-slide-bck-enter      { opacity: 0; transform: translateX(-32px); }
.ob-slide-bck-leave-to   { opacity: 0; transform: translateX(32px); }

.ob-fade-enter-active,
.ob-fade-leave-active    { transition: opacity 0.2s ease; }
.ob-fade-enter,
.ob-fade-leave-to        { opacity: 0; }

/* ── Bulk import info modal ───────────────────────────────────────── */
.ob-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(0,0,0,.4);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}
.ob-modal-panel {
    position: relative;
    width: 100%;
    max-width: 420px;
    max-height: 90vh;
    overflow-y: auto;
    background: #fff;
    border-radius: 12px;
    padding: 1.75rem 1.5rem;
    text-align: center;
}
.ob-modal-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f8f9fa;
    color: #495057;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.85rem;
}
.ob-modal-close:hover { background: #e9ecef; }
.ob-modal-icon {
    font-size: 2rem;
    color: #fd4719;
}
.ob-modal-steps {
    text-align: left;
    font-size: 0.85rem;
    color: #495057;
    padding-left: 1.25rem;
    margin: 1rem 0 1.5rem;
}
.ob-modal-steps li { list-style-type: decimal; margin-bottom: 6px; }

/* ── Activity hub cards ─────────────────────────────────────────── */
.ob-hub-cards { display: flex; flex-direction: column; gap: 10px; }

.ob-hub-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    border: 1.5px solid #dee2e6;
    border-radius: 10px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
    user-select: none;
}
.ob-hub-card:hover {
    border-color: #adb5bd;
    box-shadow: 0 2px 10px rgba(0,0,0,.06);
    transform: translateY(-1px);
}
.ob-hub-card--primary {
    border-color: #212529;
}
.ob-hub-card--primary:hover {
    border-color: #fd4719;
    box-shadow: 0 2px 10px rgba(253,71,25,.1);
}

.ob-hub-card__icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: #6c757d;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease;
}
.ob-hub-card--primary .ob-hub-card__icon-wrap {
    background: rgba(253,71,25,.08);
    color: #fd4719;
}

.ob-hub-card__body {
    flex: 1;
    min-width: 0;
}
.ob-hub-card__title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #212529;
    line-height: 1.3;
}
.ob-hub-card__desc {
    font-size: 0.76rem;
    color: #6c757d;
    line-height: 1.35;
    margin-top: 2px;
}

.ob-hub-card__arrow {
    font-size: 0.75rem;
    color: #adb5bd;
    flex-shrink: 0;
    transition: color 0.15s ease;
}
.ob-hub-card--primary .ob-hub-card__arrow { color: #212529; }
.ob-hub-card:hover .ob-hub-card__arrow    { color: #212529; }
</style>
